import type { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TrendingUp, Zap } from "lucide-react";
import type {
  PaidMediaOverviewEfficiencyGap,
  PaidMediaOverviewPlatform,
  PaidMediaOverviewRevenueContributionLeader,
  PaidMediaOverviewSummaryPlatform,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewIndex,
  formatOverviewMultiplier,
  formatOverviewPct,
  OVERVIEW_EMPTY_VALUE,
} from "../../utils/paidMediaOverviewFormat";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import { OVERVIEW_PLATFORM_DESCRIPTORS } from "../../data/paidMediaOverviewPlatforms";
import { OverviewMetricTip } from "./overviewPrimitives";

interface OverviewInsightCardsProps {
  platforms: PaidMediaOverviewSummaryPlatform[];
  revenueContributionLeader: PaidMediaOverviewRevenueContributionLeader | null;
  efficiencyGap: PaidMediaOverviewEfficiencyGap | null;
  efficiencyLaggard: PaidMediaOverviewPlatform | null;
}

const OverviewInsightCards: FC<OverviewInsightCardsProps> = ({
  platforms,
  revenueContributionLeader,
  efficiencyGap,
  efficiencyLaggard,
}) => {
  const { t } = useTranslation();

  const leaderLabel = overviewPlatformLabel(t, revenueContributionLeader?.platform);
  const gapLeaderLabel = overviewPlatformLabel(t, efficiencyGap?.leading_platform);
  const gapLaggardLabel = overviewPlatformLabel(t, efficiencyLaggard);

  return (
    <div className="paid-media-overview__insight-stack">
      <article className="paid-media-panel paid-media-overview__insight-card">
        <div className="paid-media-overview__insight-card-header">
          <span className="paid-media-overview__insight-card-label">
            {t("paidMediaOverview.comparison.revenueLeaderLabel")}
            <OverviewMetricTip
              id="revenueLeader"
              label={t("paidMediaOverview.comparison.revenueLeaderLabel")}
            />
          </span>
          <TrendingUp size={16} className="paid-media-overview__insight-card-icon" aria-hidden />
        </div>
        <div className="paid-media-overview__insight-card-value">
          {leaderLabel || OVERVIEW_EMPTY_VALUE}
        </div>
        <p className="paid-media-overview__insight-card-copy">
          <Trans
            i18nKey="paidMediaOverview.comparison.revenueLeaderCopy"
            values={{
              revenueShare: formatOverviewPct(revenueContributionLeader?.revenue_share, 0),
              spendShare: formatOverviewPct(revenueContributionLeader?.spend_share, 0),
            }}
            components={{ b: <strong /> }}
          />
        </p>
        <div className="paid-media-overview__insight-card-callout paid-media-overview__insight-card-callout--positive">
          {t("paidMediaOverview.comparison.revenueLeaderIndex", {
            value: formatOverviewIndex(revenueContributionLeader?.efficiency_index),
          })}
        </div>
      </article>

      <article className="paid-media-panel paid-media-overview__insight-card">
        <div className="paid-media-overview__insight-card-header">
          <span className="paid-media-overview__insight-card-label">
            {t("paidMediaOverview.comparison.efficiencyGapLabel")}
            <OverviewMetricTip
              id="efficiencyGap"
              label={t("paidMediaOverview.comparison.efficiencyGapLabel")}
            />
          </span>
          <Zap size={16} className="paid-media-overview__insight-card-icon" aria-hidden />
        </div>
        <div className="paid-media-overview__insight-card-value">
          {gapLeaderLabel
            ? t("paidMediaOverview.comparison.efficiencyGapValue", {
                platform: gapLeaderLabel,
                value: formatOverviewMultiplier(efficiencyGap?.value),
              })
            : OVERVIEW_EMPTY_VALUE}
        </div>
        <p className="paid-media-overview__insight-card-copy">
          {gapLeaderLabel && gapLaggardLabel
            ? t("paidMediaOverview.comparison.efficiencyGapCopy", {
                leader: gapLeaderLabel,
                laggard: gapLaggardLabel,
              })
            : t("paidMediaOverview.comparison.efficiencyGapCopyFallback")}
        </p>
        <div className="paid-media-overview__insight-card-legend">
          {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
            <span key={descriptor.key}>
              {t(descriptor.labelKey)}{" "}
              <strong>
                {formatOverviewMultiplier(
                  platforms?.find(entry => entry?.platform === descriptor.key)?.roas
                )}
              </strong>
            </span>
          ))}
        </div>
      </article>
    </div>
  );
};

export default OverviewInsightCards;
